import { Button, createDataTableColumnHelper, DataTable, Input } from "@beweegbaas/ui";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import type { FormEvent } from "react";
import { useState } from "react";
import { toast } from "@beweegbaas/ui";
import { DataTableActions } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { addSportFn, getAdminSportsOverview, removeSportFn } from "#/modules/admin/admin.functions";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/admin/sports/")({
  loader: () => getAdminSportsOverview(),
  component: SportenAdminOverview,
  errorComponent: SportsError,
});

function SportsError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type SportRow = components["schemas"]["SportModel"];

function SportRowActions({ sport }: { sport: SportRow }) {
  const router = useRouter();
  const removeSport = useServerFn(removeSportFn);

  return (
    <DataTableActions
      actions={[
        {
          key: "delete",
          label: m["admin.sporten.delete.confirm_title"](),
          icon: <IconMaterialSymbolsDelete />,
          destructive: true,
          confirm: {
            title: m["admin.sporten.delete.confirm_title"](),
            message: m["admin.sporten.delete.confirm_message"]({ sportName: sport.name }),
          },
          onClick: async () => {
            await removeSport({ data: { sportId: sport.id } });
            toast.success("Sport verwijderd.");
            await router.invalidate();
          },
        },
      ]}
    />
  );
}

const helper = createDataTableColumnHelper<SportRow>();

const columns = helper.columns([
  helper.accessor("name", {
    header: () => m["common.fields.name"](),
  }),
  helper.display({
    id: "actions",
    meta: { action: true, headerClassName: "w-0" },
    cell: ({ row }) => <SportRowActions sport={row.original} />,
  }),
]);

function SportenAdminOverview() {
  const { sporten } = Route.useLoaderData();
  const router = useRouter();
  const addSport = useServerFn(addSportFn);
  const [newSportName, setNewSportName] = useState("");
  const [isPending, setIsPending] = useState(false);

  const handleAddSport = async (e: FormEvent) => {
    e.preventDefault();
    const name = newSportName.trim();
    if (!name) return;
    setIsPending(true);
    try {
      await addSport({ data: { name } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    } finally {
      setIsPending(false);
    }
    toast.success(`Sport ${name} toegevoegd.`);
    setNewSportName("");
    await router.invalidate();
  };

  return (
    <Page>
      <Page.Header>
        <Page.Header.Actions>
          <Page.Header.MainAction>
            <form
              onSubmit={handleAddSport}
              className="flex items-center gap-2 max-md:flex-col max-md:items-stretch"
            >
              <Input
                type="text"
                placeholder={m["admin.sporten.add_placeholder"]()}
                value={newSportName}
                onChange={(e) => setNewSportName(e.target.value)}
                className="min-w-[200px] grow"
                disabled={isPending}
              />
              <Button size="xl" type="submit" disabled={!newSportName.trim()} isPending={isPending}>
                <IconMaterialSymbolsAdd />
                {m["admin.sporten.add_button"]()}
              </Button>
            </form>
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <DataTable
        alignToContainer
        aria-label={m["admin.sporten.title"]()}
        columns={columns}
        data={sporten}
        getRowId={(sport) => sport.id}
      />
    </Page>
  );
}
